import { usePageHeader } from "contexts/PageHeaderContext";
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { InputFieldset } from 'ui/inputFieldset';
import { Label } from '../../../ui/label';
import { Switch } from '../../../ui/switch';
import { Calendar, } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CreateMiqaatPayload, FormData, FormErrors } from '../../../types/miqaatConfig';
import { toast } from 'sonner';
import { createMiqaatConfig } from 'services/miqaatConfigService';
import { AxiosError } from 'axios';
import { useScreenPermission } from 'hooks/useScreenPermission';
import { allowOnlyNumbers } from 'utils/commonutils';


export function AddMiqaatConfiguration() {

  const navigate = useNavigate()
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: 'Add New Miqaat',
      childTitle: 'Fill in the miqaat details below',
      icon: <Calendar className="h-5 w-5" />,
      onBack: () => { navigate(-1) },
    });
    return () => setPageHeader(null);
  }, [setPageHeader, navigate]);
  const { canAdd } = useScreenPermission();
  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<FormData>({
    miqaatName: '',
    miqaatCode: '',
    cutoffDays: 30,
    totalPaxLimit: 0,
    razaPackage: false,
    isActive: false,
    itsMiqaatId: '',
  });


  // VALIDATION FUNCTION START
  const validateForm = () => {
    const newErrors: FormErrors = {};
    if (!formData.miqaatName.trim()) {
      newErrors.miqaatName = "Miqaat name is required";
    }
    if (!formData.miqaatCode.trim()) {
      newErrors.miqaatCode = "Miqaat code is required";
    }
    if (formData.cutoffDays <= 0) {
      newErrors.cutoffDays = "Cutoff days must be greater than 0";
    }
    if (formData.totalPaxLimit <= 0) {
      newErrors.totalPaxLimit = "Total pax limit is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) return;
    try {
      const payload: CreateMiqaatPayload = {
        miqaatName: formData.miqaatName,
        miqaatCode: formData.miqaatCode,
        // description: formData.description,
        cutoffDays: Number(formData.cutoffDays),
        totalPaxLimit: Number(formData.totalPaxLimit),
        requireRaza: formData.razaPackage,
        isActive: formData.isActive,
        createdBy: 0,
      };
      //API CALL
      await createMiqaatConfig(payload);
      toast.success("Miqaat created successfully");
      navigate("/miqaat-master")
    } catch (err: unknown) {
      let errorMessage = "Failed to create miqaat";
      if (err instanceof AxiosError) {
        const data = err.response?.data;
        errorMessage = data?.message || err.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    }
  };


  // FORM VIEW
  return (
    <div className="space-y-4 pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => { navigate(-1) }}
            className="h-9"
          >
            Cancel
          </Button>
          {canAdd && (
            <Button
              onClick={handleSubmit}
              style={{ background: '#5B9BD5' }}
              className="text-white h-9 px-8"
            >
              {'Create Miqaat'}
            </Button>
          )}
        </div>
      </div>

      {/* Basic Information Card */}
      <Card className="bg-white border-[#5B9BD5]/20 shadow-md">
        <CardHeader style={{ backgroundColor: '#E3F2FD' }} className="pb-3">
          <CardTitle className="text-gray-900 text-base">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-5">
          <div className="space-y-5">
            {/* Miqaat Name & Code */}
            <div>
              <h3 className="text-sm mb-3" style={{ color: '#5B9BD5' }}>Miqaat Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <InputFieldset
                    label="Miqaat Name"
                    required
                    id="miqaatName"
                    maxLength={30}
                    placeholder="Enter miqaat name..."
                    value={formData.miqaatName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, miqaatName: value });
                      if (value.trim()) {
                        setErrors({ ...errors, miqaatName: undefined });
                      }
                    }}
                    className="bg-input-background bg-white border-gray-200"
                  />
                  {errors.miqaatName && (
                    <p className="text-red-500 text-xs mt-1">{errors.miqaatName}</p>
                  )}
                  <span className="text-xs text-red-500">
                    {formData.miqaatName.length}/30 
                    {formData.miqaatName.length >= 30 && (
                      <span className="text-xs text-red-500">
                        Maximum 30 characters allowed
                      </span>
                    )}
                  </span>
                </div>
                <div className="space-y-1.5">
                  <InputFieldset
                    label="Miqaat Code"
                    id="miqaatCode"
                    placeholder="e.g., MQT-2025-001"
                    value={formData.miqaatCode}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, miqaatCode: value });
                      if (value.trim()) {
                        setErrors({ ...errors, miqaatCode: undefined });
                      }
                    }}
                    className="bg-input-background bg-white border-gray-200"
                  />
                  {errors.miqaatCode && (
                    <p className="text-red-500 text-xs mt-1">{errors.miqaatCode}</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm mb-3" style={{ color: '#5B9BD5' }}>Validity Period</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <InputFieldset
                    label="Cutoff Days"
                    id="cutoffDays"
                    type="text"
                    value={formData.cutoffDays}
                    onChange={(e) => {
                      const value = allowOnlyNumbers(e.target.value);
                      setFormData({ ...formData, cutoffDays: parseInt(value) || 0 });
                      if (value.trim()) {
                        setErrors({ ...errors, cutoffDays: undefined });
                      }
                    }}
                    className="bg-white border-gray-200"
                  />
                  {errors.cutoffDays && (
                    <p className="text-red-500 text-xs mt-1">{errors.cutoffDays}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <InputFieldset
                    label="Total Pax Limit"
                    required
                    id="totalPaxLimit"
                    type="text"
                    value={formData.totalPaxLimit}
                    onChange={(e) => {
                      const value = allowOnlyNumbers(e.target.value);
                      setFormData({ ...formData, totalPaxLimit: parseInt(value) || 0 });
                      if (value.trim()) {
                        setErrors({ ...errors, totalPaxLimit: undefined });
                      }
                    }}
                    className="bg-white border-gray-200"
                  />
                  {errors.totalPaxLimit && (
                    <p className="text-red-500 text-xs mt-1">{errors.totalPaxLimit}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Toggles - Require Raza & Is Active */}
            <div>
              <h3 className="text-sm mb-3" style={{ color: '#5B9BD5' }}>Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Require Raza Toggle */}
                <div className="bg-input-background flex items-center justify-between p-3 bg-white rounded border border-gray-200">
                  <div className="space-y-0.5">
                    <Label className="text-sm text-gray-900">Require Raza</Label>
                    <p className="text-xs text-gray-500">Requires Raza validation via ITS API</p>
                  </div>
                  <Switch
                    checked={formData.razaPackage}
                    onCheckedChange={(checked) => setFormData({ ...formData, razaPackage: checked })}
                    className="data-[state=checked]:bg-[#5B9BD5]"
                  />
                </div>

                {/* Is Active Toggle */}
                <div className="bg-input-background flex items-center justify-between p-3 bg-white rounded border border-gray-200">
                  <div className="space-y-0.5">
                    <Label className="text-sm text-gray-900">Is Active</Label>
                    <p className="text-xs text-gray-500">Activate this miqaat package</p>
                  </div>
                  <Switch
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                    className="data-[state=checked]:bg-[#5B9BD5]"
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}